import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import GeneratePostModal from "@/components/blog/GeneratePostModal";
import ContentBlockRenderer from "@/components/blog/ContentBlockRenderer";
import BlogContent from "@/components/blog/BlogContent";
import { Sparkles, Save, Eye, ArrowLeft, ImagePlus, Loader2, Trash2 } from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string;
}

const AdminPostEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [coverImageAlt, setCoverImageAlt] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [authorName, setAuthorName] = useState("IESSA");
  const [seoKeywords, setSeoKeywords] = useState("");
  const [tone, setTone] = useState("pastoral");
  const [categories, setCategories] = useState<Category[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imagePrompts, setImagePrompts] = useState<string[]>([]);
  const [contentJson, setContentJson] = useState<any>(null);

  useEffect(() => {
    if (sessionStorage.getItem("iessa-admin") !== "true") {
      navigate("/admin", { replace: true });
      return;
    }
    supabase.from("categories").select("id, name, slug").order("name").then(({ data }) => setCategories(data || []));
    if (isEdit && id) {
      supabase.from("posts").select("*").eq("id", id).maybeSingle().then(({ data }) => {
        if (data) {
          setTitle(data.title);
          setSlug(data.slug);
          setExcerpt(data.excerpt || "");
          setContentHtml(data.content_html);
          setCoverImageUrl(data.cover_image_url || "");
          setCoverImageAlt(data.cover_image_alt || "");
          setCategoryId(data.category_id || "");
          setAuthorName(data.author_name);
          setSeoKeywords(data.seo_keywords || "");
          setTone(data.tone || "pastoral");
          if (data.content_json) setContentJson(data.content_json);
        }
      });
    }
  }, [id, isEdit, navigate]);

  const generateSlug = useCallback((text: string) => {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 80);
  }, []);

  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (!isEdit || !slug) setSlug(generateSlug(val));
  };

  const handleSave = async (status: "draft" | "published") => {
    if (!title.trim() || !slug.trim()) {
      toast({ title: "Título e slug são obrigatórios", variant: "destructive" });
      return;
    }
    setSaving(true);
    const postData: any = {
      title,
      slug,
      content_html: contentHtml,
      excerpt: excerpt || null,
      cover_image_url: coverImageUrl || null,
      cover_image_alt: coverImageAlt || null,
      category_id: categoryId || null,
      author_name: authorName,
      seo_keywords: seoKeywords || null,
      tone,
      status,
      published_at: status === "published" ? new Date().toISOString() : null,
    };
    if (contentJson) {
      postData.content_json = contentJson;
    }

    let error;
    if (isEdit && id) {
      ({ error } = await supabase.from("posts").update(postData).eq("id", id));
    } else {
      ({ error } = await supabase.from("posts").insert(postData));
    }

    if (error) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: status === "published" ? "Post publicado!" : "Rascunho salvo!" });
      navigate("/admin/dashboard");
    }
    setSaving(false);
  };

  const handleGenerated = (data: { title: string; slug: string; excerpt: string; content_html: string; seo_keywords: string; image_prompts: string[] }) => {
    setTitle(data.title);
    setSlug(data.slug);
    setExcerpt(data.excerpt);
    setContentHtml(data.content_html);
    setSeoKeywords(data.seo_keywords);
    setImagePrompts(data.image_prompts || []);
  };

  const generateCoverImage = async (prompt: string) => {
    setGeneratingImage(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-blog-image", {
        body: { prompt },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCoverImageUrl(data.url);
      setCoverImageAlt(prompt);
      toast({ title: "Imagem gerada!" });
    } catch (e) {
      toast({ title: "Erro ao gerar imagem", description: e instanceof Error ? e.message : "Tente novamente.", variant: "destructive" });
    } finally {
      setGeneratingImage(false);
    }
  };

  return (
    <section className="section-shell">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/dashboard")}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
        </Button>
        <h1 className="text-xl font-bold text-foreground">{isEdit ? "Editar Post" : "Novo Post"}</h1>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Editor */}
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowGenerate(true)}>
              <Sparkles className="mr-1 h-4 w-4" /> Gerar com IA
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="mr-1 h-4 w-4" /> {showPreview ? "Editor" : "Preview"}
            </Button>
          </div>

          <div className="space-y-1">
            <Label>Título *</Label>
            <Input value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Título do artigo" />
          </div>

          <div className="space-y-1">
            <Label>Slug *</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="url-do-artigo" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Categoria</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Autor</Label>
              <Input value={authorName} onChange={(e) => setAuthorName(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Resumo (Meta Descrição)</Label>
            <Textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} placeholder="Breve resumo para SEO" />
          </div>

          <div className="space-y-1">
            <Label>Conteúdo HTML</Label>
            <Textarea value={contentHtml} onChange={(e) => setContentHtml(e.target.value)} rows={16} className="font-mono text-xs" placeholder="<h2>Seção...</h2><p>Conteúdo...</p>" />
          </div>

          <div className="space-y-1">
            <Label>Palavras-chave SEO</Label>
            <Input value={seoKeywords} onChange={(e) => setSeoKeywords(e.target.value)} placeholder="oração, fé, esperança" />
          </div>

          {/* Cover image */}
          <div className="space-y-2">
            <Label>Imagem de Capa</Label>
            {coverImageUrl && (
              <img src={coverImageUrl} alt={coverImageAlt || "Capa"} className="w-full max-h-48 object-cover rounded-lg" />
            )}
            <div className="space-y-1">
              <Input value={coverImageUrl} onChange={(e) => setCoverImageUrl(e.target.value)} placeholder="URL da imagem" />
            </div>
            {imagePrompts.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Sugestões de imagem da IA:</p>
                {imagePrompts.map((p, i) => (
                  <Button key={i} variant="outline" size="sm" className="mr-1 mb-1 text-xs" onClick={() => generateCoverImage(p)} disabled={generatingImage}>
                    {generatingImage ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <ImagePlus className="mr-1 h-3 w-3" />}
                    {p.slice(0, 50)}...
                  </Button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-4">
            <Button onClick={() => handleSave("draft")} variant="outline" disabled={saving}>
              <Save className="mr-1 h-4 w-4" /> Salvar rascunho
            </Button>
            <Button onClick={() => handleSave("published")} disabled={saving}>
              Publicar
            </Button>
            {isEdit && id && (
              <Button
                variant="ghost"
                className="ml-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                disabled={saving}
                onClick={async () => {
                  if (!window.confirm("Tem certeza que deseja excluir este post permanentemente?")) return;
                  const { error } = await supabase.from("posts").delete().eq("id", id);
                  if (error) {
                    toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
                  } else {
                    toast({ title: "Post excluído" });
                    navigate("/admin/dashboard");
                  }
                }}
              >
                <Trash2 className="mr-1 h-4 w-4" /> Excluir
              </Button>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className={`${showPreview ? "block" : "hidden lg:block"}`}>
          <div className="card-ambient p-6 sticky top-24">
            <p className="text-xs font-medium uppercase tracking-wide text-primary mb-4">Preview</p>
            {coverImageUrl && (
              <img src={coverImageUrl} alt={coverImageAlt || "Capa"} className="w-full rounded-lg mb-4 object-cover max-h-48" />
            )}
            <h2 className="text-2xl font-bold text-foreground mb-2">{title || "Título do artigo"}</h2>
            {excerpt && <p className="text-sm text-muted-foreground mb-4">{excerpt}</p>}
            {contentJson?.content_blocks ? (
              <ContentBlockRenderer
                blocks={contentJson.content_blocks}
                editable={true}
                onBlocksChange={(newBlocks) => {
                  setContentJson({ ...contentJson, content_blocks: newBlocks });
                }}
              />
            ) : contentHtml ? (
              <BlogContent html={contentHtml} />
            ) : (
              <p className="text-muted-foreground text-sm">O conteúdo aparecerá aqui...</p>
            )}
          </div>
        </div>
      </div>

      <GeneratePostModal open={showGenerate} onOpenChange={setShowGenerate} onGenerated={handleGenerated} />
    </section>
  );
};

export default AdminPostEditor;
